/**
 * Passkey (WebAuthn) service for browser-side credential management.
 * Communicates with api-server for challenge generation and verification.
 */

import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { auth } from './firebase';

const API_BASE = import.meta.env.VITE_API_URL || '';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

/**
 * Server sentinel (403) from the passkey-register endpoints when the session is
 * too old to bind a new authenticator. Surfaced to callers as the thrown
 * error's message so the account UI can reauthenticate and retry, mirroring the
 * account-delete / email-change flows.
 */
export const REAUTH_REQUIRED = 'requires_recent_login';

/** True if a non-OK response is the recent-login gate (403 + sentinel body). */
async function isRecentLoginGate(res: Response): Promise<boolean> {
  if (res.status !== 403) return false;
  try {
    const body = (await res.json()) as { message?: string };
    return body?.message === REAUTH_REQUIRED;
  } catch {
    return false;
  }
}

/**
 * Register a passkey for the currently signed-in user.
 * Returns true if registration succeeded, false if user cancelled.
 */
export async function registerPasskey(): Promise<boolean> {
  // 1. Get registration options from server
  const authHeaders = await getAuthHeaders();
  const optionsRes = await fetch(`${API_BASE}/api/auth/passkey/register/options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
  });

  if (!optionsRes.ok) {
    if (await isRecentLoginGate(optionsRes)) throw new Error(REAUTH_REQUIRED);
    throw new Error('Failed to get passkey registration options');
  }

  const { options, session_id } = await optionsRes.json();
  // Server wraps in {publicKey: ...} — simplewebauthn v11 expects the inner object
  const registrationOptions = options.publicKey || options;

  // 2. Prompt browser credential creation
  let attestation;
  try {
    attestation = await startRegistration({ optionsJSON: registrationOptions });
  } catch (err) {
    // User cancelled or browser doesn't support WebAuthn
    console.warn('Passkey registration prompt dismissed or failed:', err);
    return false;
  }

  // 3. Verify with server
  const verifyRes = await fetch(`${API_BASE}/api/auth/passkey/register/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ session_id, credential: attestation }),
  });

  if (!verifyRes.ok) {
    if (await isRecentLoginGate(verifyRes)) throw new Error(REAUTH_REQUIRED);
    throw new Error('Failed to verify passkey registration');
  }

  return true;
}

/**
 * Authenticate with a passkey. Returns a Firebase custom token on success.
 */
export async function authenticateWithPasskey(email: string): Promise<string> {
  // 1. Get authentication options from server
  const optionsRes = await fetch(`${API_BASE}/api/auth/passkey/authenticate/options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!optionsRes.ok) {
    const err = await optionsRes.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Failed to get passkey authentication options');
  }

  const { options, session_id } = await optionsRes.json();
  const authOptions = options.publicKey || options;

  // 2. Prompt browser credential assertion
  const assertion = await startAuthentication({ optionsJSON: authOptions });

  // 3. Verify with server — returns Firebase custom token
  const verifyRes = await fetch(`${API_BASE}/api/auth/passkey/authenticate/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id, credential: assertion }),
  });

  if (!verifyRes.ok) {
    const err = await verifyRes.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Passkey authentication failed');
  }

  const { custom_token } = await verifyRes.json();
  return custom_token;
}
